import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { verifyCaptcha } from '@/api/captcha';

interface ReCaptchaVerificationProps {
	onVerificationComplete?: (success: boolean) => void;
	trigger?: boolean;
	className?: string;
}

const ReCaptchaVerification: React.FC<ReCaptchaVerificationProps> = ({
	onVerificationComplete,
	trigger = false,
	className = '',
}) => {
	const [verifying, setVerifying] = useState(false);
	const [verified, setVerified] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const RECAPTCHA_SITE_KEY = import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string;

	useEffect(() => {
		if (!document.querySelector('script[src*="recaptcha"]')) {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
			script.async = true;
			document.head.appendChild(script);
		}
	}, []);

	const handleRetry = () => {
		setVerified(false);
		setError(null);
		setVerifying(false);
	};

	useEffect(() => {
		const verify = async () => {
			if (trigger && !verified && !verifying) {
				setVerifying(true);
				setError(null);
				try {
					if (!window.grecaptcha) {
						throw new Error('reCAPTCHA not loaded');
					}

					const result = await new Promise<boolean>((resolve) => {
						window.grecaptcha.ready(async () => {
							try {
								const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
									action: 'verify',
								});
								const response = await verifyCaptcha(token);

								if (response && response.success) {
									setVerified(true);
									setError(null);
									resolve(true);
								} else {
									setError('Verification failed. Please try again later.');
									resolve(false);
								}
							} catch {
								setError('Verification failed. Please try again later.');
								resolve(false);
							}
						});
					});

					onVerificationComplete?.(result);
				} catch (error) {
					console.error('reCAPTCHA error:', error);
					setError('Verification failed. Please try again later.');
					onVerificationComplete?.(false);
				} finally {
					setVerifying(false);
				}
			}
		};
		verify();
	}, [trigger]);

	if (!trigger) {
		return null;
	}

	return (
		<div className={`text-sm text-center ${className}`}>
			{verified ? (
				<div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
					<CheckCircle className="h-5 w-5" />
					<span className="font-medium">Human verification completed.</span>
				</div>
			) : error ? (
				<div className="flex flex-col items-center justify-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg">
					<div className="flex items-center gap-2">
						<AlertCircle className="h-5 w-5" />
						<span className="font-medium">{error}</span>
					</div>
					<button
						onClick={handleRetry}
						className="flex items-center gap-2 mt-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
					>
						<RefreshCw className="h-4 w-4" />
						Try Again
					</button>
				</div>
			) : (
				<div className="text-gray-500 flex items-center justify-center gap-2 p-4">
					<div
						role="status"
						aria-label="Loading"
						className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"
					/>
					<span>Verifying you're human...</span>
				</div>
			)}
		</div>
	);
};

export default ReCaptchaVerification;
